/**
 * Feeds API - Reactions
 *
 * Like, comment, update, and remove reactions.
 */

import { z } from 'zod'
import { StreamResultSchema, StreamRemoveResultSchema, ReactionSchema, CommentSchema } from './common'

// ============================================================================
// POST /v1/feeds/activities/:activityId/like
// ============================================================================

export const ReactionAddLikeRequestSchema = z.object({
  activity_id: z.string().min(1),
  user_id: z.string().optional()
})

export type ReactionAddLikeRequest = z.infer<typeof ReactionAddLikeRequestSchema>

export const ReactionAddLikeResponseSchema = z.object({
  message: z.string(),
  data: StreamResultSchema
})

export type ReactionAddLikeResponse = z.infer<typeof ReactionAddLikeResponseSchema>

// ============================================================================
// POST /v1/feeds/activities/:activityId/comments
// ============================================================================

export const ReactionAddCommentRequestSchema = z.object({
  activity_id: z.string().min(1),
  text: z.string().min(1).max(2000),
  user_id: z.string().optional()
})

export type ReactionAddCommentRequest = z.infer<typeof ReactionAddCommentRequestSchema>

export const ReactionAddCommentResponseSchema = z.object({
  message: z.string(),
  data: StreamResultSchema
})

export type ReactionAddCommentResponse = z.infer<typeof ReactionAddCommentResponseSchema>

// ============================================================================
// POST /v1/feeds/reactions/:reactionId/like (child like)
// ============================================================================

export const ReactionAddChildLikeRequestSchema = z.object({
  reaction_id: z.string().min(1),
  user_id: z.string().optional()
})

export type ReactionAddChildLikeRequest = z.infer<typeof ReactionAddChildLikeRequestSchema>

export const ReactionAddChildLikeResponseSchema = z.object({
  message: z.string(),
  data: StreamResultSchema
})

export type ReactionAddChildLikeResponse = z.infer<typeof ReactionAddChildLikeResponseSchema>

// ============================================================================
// POST /v1/feeds/reactions/:reactionId/comment (child comment)
// ============================================================================

export const ReactionAddChildCommentRequestSchema = z.object({
  reaction_id: z.string().min(1),
  text: z.string().min(1),
  user_id: z.string().optional()
})

export type ReactionAddChildCommentRequest = z.infer<typeof ReactionAddChildCommentRequestSchema>

export const ReactionAddChildCommentResponseSchema = z.object({
  message: z.string(),
  data: StreamResultSchema
})

export type ReactionAddChildCommentResponse = z.infer<typeof ReactionAddChildCommentResponseSchema>

// ============================================================================
// PUT /v1/feeds/reactions/:reactionId
// ============================================================================

export const ReactionUpdateCommentRequestSchema = z.object({
  reaction_id: z.string().min(1),
  text: z.string().min(1),
  user_id: z.string().optional()
})

export type ReactionUpdateCommentRequest = z.infer<typeof ReactionUpdateCommentRequestSchema>

export const ReactionUpdateCommentResponseSchema = z.object({
  message: z.string(),
  data: StreamResultSchema
})

export type ReactionUpdateCommentResponse = z.infer<typeof ReactionUpdateCommentResponseSchema>

// ============================================================================
// DELETE /v1/feeds/reactions/:reactionId
// ============================================================================

export const ReactionRemoveRequestSchema = z.object({
  reaction_id: z.string().min(1)
})

export type ReactionRemoveRequest = z.infer<typeof ReactionRemoveRequestSchema>

export const ReactionRemoveResponseSchema = z.object({
  message: z.string(),
  data: StreamRemoveResultSchema
})

export type ReactionRemoveResponse = z.infer<typeof ReactionRemoveResponseSchema>

// ============================================================================
// DELETE /v1/feeds/activities/:activityId/like  (Stream Feeds v3 unlike)
// v3 likes are keyed on `(activity_id, type, user_id)` — no stable reaction id.
// ============================================================================

export const ReactionRemoveLikeRequestSchema = z.object({
  activity_id: z.string().min(1),
  user_id: z.string().optional()
})

export type ReactionRemoveLikeRequest = z.infer<typeof ReactionRemoveLikeRequestSchema>

export const ReactionRemoveLikeResponseSchema = z.object({
  message: z.string(),
  data: z.object({ removed: z.boolean() })
})

export type ReactionRemoveLikeResponse = z.infer<typeof ReactionRemoveLikeResponseSchema>

// ============================================================================
// DELETE /v1/feeds/comments/:commentId/like  (Stream Feeds v3 comment-unlike)
// Symmetric with the activity unlike — `(comment_id, type, user_id)` keyed.
// ============================================================================

export const ReactionRemoveChildLikeRequestSchema = z.object({
  comment_id: z.string().min(1),
  user_id: z.string().optional()
})

export type ReactionRemoveChildLikeRequest = z.infer<typeof ReactionRemoveChildLikeRequestSchema>

export const ReactionRemoveChildLikeResponseSchema = z.object({
  message: z.string(),
  data: z.object({ removed: z.boolean() })
})

export type ReactionRemoveChildLikeResponse = z.infer<typeof ReactionRemoveChildLikeResponseSchema>

// ============================================================================
// Legacy-style schemas (alternative naming)
// ============================================================================

export const LikeActivityRequestSchema = z.object({
  activityId: z.string()
})

export type LikeActivityRequest = z.infer<typeof LikeActivityRequestSchema>

export const LikeActivityResponseSchema = z.object({
  data: ReactionSchema,
  message: z.string()
})

export type LikeActivityResponse = z.infer<typeof LikeActivityResponseSchema>

export const AddCommentRequestSchema = z.object({
  text: z.string(),
  parentId: z.string().optional()
})

export type AddCommentRequest = z.infer<typeof AddCommentRequestSchema>

export const AddCommentResponseSchema = z.object({
  data: CommentSchema,
  message: z.string()
})

export type AddCommentResponse = z.infer<typeof AddCommentResponseSchema>

export const GetActivityReactionsResponseSchema = z.object({
  data: z.object({
    likes: z.array(ReactionSchema),
    comments: z.array(CommentSchema)
  }),
  message: z.string()
})

export type GetActivityReactionsResponse = z.infer<typeof GetActivityReactionsResponseSchema>

export const UpdateReactionRequestSchema = z.object({
  text: z.string()
})

export type UpdateReactionRequest = z.infer<typeof UpdateReactionRequestSchema>

export const UpdateReactionResponseSchema = z.object({
  data: ReactionSchema,
  message: z.string()
})

export type UpdateReactionResponse = z.infer<typeof UpdateReactionResponseSchema>

export const DeleteReactionResponseSchema = z.object({
  message: z.string()
})

export type DeleteReactionResponse = z.infer<typeof DeleteReactionResponseSchema>
